import React, {
    useCallback, useEffect, useState
}                                     from 'react'
import {
    Box, Button, Container,
    FormControl, Grid, InputLabel, Link, MenuItem, Paper, Select,
    Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
}                                     from '@mui/material'
import { Link as RouterLink }         from 'react-router-dom'
import { useTranslation }             from 'react-i18next'
import { useSnackbar }                from 'notistack'
import Page                           from '../../components/Page'
import { useApi }                     from '../../utils/api'
import { dateToUserString }           from '../../utils/dateutils'

// message.operator keys (gateway Consts.Operator.Keys)
const operators = [
    { key: 'O', name: 'Orange' },
    { key: 'T', name: 'Telekom' },
    { key: '2', name: 'O2' },
    { key: 'S', name: '4ka' },
]

/**
 * Fake provider (development only) - receive sms with delivery status of its answers
 */
export default function FakeProviderPage () {
    const api = useApi()
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const [telFrom, setTelFrom] = useState('+421900000000')
    const [telTo, setTelTo] = useState('')
    const [operator, setOperator] = useState('O')
    const [content, setContent] = useState('')
    const [deliveryStatus, setDeliveryStatus] = useState('delivered')
    const [messages, setMessages] = useState([])
    const [statuses, setStatuses] = useState([])

    const loadMessages = useCallback(() => {
        api.fake.messages()
            .then(response => {
                setMessages(response.data)
                setStatuses(response.statuses)
            })
            .catch(e => enqueueSnackbar(e.error ?? t('Failed to load messages'), { variant: 'error' }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        loadMessages()
        // answers & auto delivery status are processed in background
        const interval = setInterval(loadMessages, 3000)
        return () => clearInterval(interval)
    }, [loadMessages])

    const receive = async () => {
        if (!telFrom.trim() || !telTo.trim() || !content.trim()) {
            enqueueSnackbar(t('Sender, recipient and content are required'), { variant: 'error' })
            return
        }
        try {
            const response = await api.fake.receive({ from: telFrom.trim(), to: telTo.trim(), operator, content, deliveryStatus: deliveryStatus || null })
            enqueueSnackbar(`${t('Message received')}: ${response.id}`, { variant: 'success' })
            setContent('')
            loadMessages()
        } catch (e) {
            enqueueSnackbar(e.error ?? t('Failed to receive message'), { variant: 'error' })
        }
    }

    return (
        <Page title={t('Fake provider')}>
            <Container>
                <Stack direction="row" alignItems="center" mb={4} spacing={2}>
                    <Typography variant="h4" gutterBottom>{t('Fake provider')}</Typography>
                </Stack>
                <Paper sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <TextField label={t('Sender')} value={telFrom} onChange={(e) => setTelFrom(e.target.value)} fullWidth/>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField label={t('Recipient')} value={telTo} onChange={(e) => setTelTo(e.target.value)} fullWidth/>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel id="fake-operator-label">{t('Operator')}</InputLabel>
                                <Select labelId="fake-operator-label" label={t('Operator')} value={operator} onChange={(e) => setOperator(e.target.value)}>
                                    {operators.map(it => <MenuItem key={it.key} value={it.key}>{it.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel id="fake-delivery-status-label">{t('Delivery status')}</InputLabel>
                                <Select labelId="fake-delivery-status-label" label={t('Delivery status')} value={deliveryStatus} onChange={(e) => setDeliveryStatus(e.target.value)}>
                                    {statuses.map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                                    <MenuItem value="">{t('None')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label={t('Sms content')} multiline minRows={3} value={content} onChange={(e) => setContent(e.target.value)} fullWidth/>
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 2 }}>
                        <Button variant="contained" onClick={receive}>{t('Receive')}</Button>
                    </Box>
                </Paper>

                <Paper sx={{ mt: 3, overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>{t('Created')}</TableCell>
                                <TableCell>{t('Sender')}</TableCell>
                                <TableCell>{t('Recipient')}</TableCell>
                                <TableCell>{t('Content')}</TableCell>
                                <TableCell>{t('Status')}</TableCell>
                                <TableCell>{t('Delivery status')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {messages.map(msg => (
                                <TableRow key={msg.id}>
                                    <TableCell>
                                        <Link component={RouterLink} to={`/dashboard/messages/${msg.id}`}>{msg.id}</Link>
                                        {msg.outgoing ? ' ←' : ' →'}
                                    </TableCell>
                                    <TableCell>{dateToUserString(msg.created)}</TableCell>
                                    <TableCell>{msg.telFrom}</TableCell>
                                    <TableCell>{msg.telTo}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</TableCell>
                                    <TableCell>{msg.processingStatus ?? t('scheduled')}</TableCell>
                                    <TableCell>{msg.deliveryStatus ?? ''}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            </Container>
        </Page>
    )
}
